import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MaterialsFacadeService } from '../services/materials.facade.service';
import { FenceService } from '../../../../../services/configurators-2d/applications/fence.service';
import { MaterialSubStepService } from '../services/material-sub-step.service';
import { SubStepEnum } from '../models/sub-step.enum';
import { Configuration } from '../../../../../models/configurators-2d/applications/fence/configuration';
import { Door } from '../../../../../models/configurators-2d/applications/fence/door';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { DoorStateService } from '../../services/door.state.service';

@Component({
  selector: 'ng-casto-materials-doors',
  templateUrl: './materials-doors.component.html',
  styleUrl: './materials-doors.component.scss',
})
export class MaterialsDoorsComponent implements OnInit {
  /** Langue sélectionnée */
  currentLang: string;

  /** Base URL de l'API */
  baseImgURL: string = undefined;
  protected readonly SubStepEnum = SubStepEnum;
  private readonly translateService = inject(TranslateService);
  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fenceService = inject(FenceService);
  private readonly materialSubStepService = inject(MaterialSubStepService);
  private readonly doorStateService = inject(DoorStateService);

  constructor() {
    this.baseImgURL = this.fenceService.apiUrl;
  }

  /**
   * Retourne le message de déréférencement
   */
  protected get disableMessage(): string {
    return this.materialsFacadeService.disableMessage();
  }

  /**
   * Retourne la configuration actuelle
   */
  protected get configuration(): Configuration {
    return this.materialsFacadeService.configuration();
  }

  /**
   * Retourne la sous-étape courante du composant
   * @protected
   */
  protected get subStep(): SubStepEnum {
    return this.materialSubStepService.subStep();
  }

  /**
   * Retourne la liste des portillons disponibles
   */
  protected get doors(): Door[] {
    return this.doorStateService.doors();
  }

  /**
   * Check si tous les portillons sont valides
   * @protected
   */
  protected get isDoorsValid(): boolean {
    return this.materialsFacadeService.isDoorsValid();
  }

  ngOnInit(): void {
    this.currentLang = this.translateService.getBrowserLang();
  }

  /**
   * Gère l'affichage/masquage des sous-menus en accordéon
   * @param step - Numéro du sous-menu (1: Lames, 2: Poteaux, 3: Lames décoratives, 4: Décors, 5: Panneaux, 6: Portillons)
   */
  protected toggleCollapse(step: SubStepEnum): void {
    this.materialSubStepService.toggleCollapse(step);
  }

  /**
   * Vérifie si la configuration comporte au moins un portillon
   * @returns true si au moins un portillon est prévu, false sinon
   */
  protected hasDoor(): boolean {
    return this.materialsFacadeService.hasDoor();
  }

  /**
   * Gère le clic sur un portillon (ajout/retrait de la configuration)
   * @param door - Le portillon concerné
   */
  protected selectDoor(door: any): void {
    // on ne peut pas activer un portillon désactivé, mais on peut le désélectionner
    if (this.isDoorDisabled(door) && !this.configuration.doors.some(d => d.id === door.id)) {
      return;
    }

    this.materialsFacadeService
      .selectDoor(door)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur click door:', err),
      });
  }

  /**
   * Vérifie si un portillon est actuellement sélectionné dans la configuration
   * @param door - Portillon
   * @returns true si le portillon est sélectionné, false sinon
   */
  protected isDoorSelected(door: Door): boolean {
    return this.materialsFacadeService.isDoorSelected(door);
  }

  /**
   * Détermine si un portillon doit être désactivé (non sélectionnable)
   * Un portillon est désactivé si la quantité maximale est atteinte
   * @param door - Le portillon à vérifier
   * @returns true si le portillon doit être désactivé, false sinon
   */
  protected isDoorDisabled(door: Door): boolean {
    return this.materialsFacadeService.isDoorDisabled(door);
  }

  /**
   * Modifie la quantité d'un portillon
   * @param door - Le portillon concerné
   * @param event - L'événement de changement
   */
  protected changeDoorQuantity(door: Door, event: Event): void {
    this.materialsFacadeService
      .changeDoorQuantity(door, event)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement quantité door:', err),
      });
  }

  /**
   * Calcule le nombre maximum de portillons de ce type autorisés
   * Prend en compte le nombre de sections nécessitant un portillon et les portillons déjà placés
   * @param door - Le portillon pour lequel calculer le maximum
   * @returns Le nombre maximum de portillons autorisés (incluant la quantité déjà saisie)
   */
  protected maxDoor(door: Door): number {
    return this.materialsFacadeService.maxDoor(door);
  }
}
